import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, startWith, map } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';

export interface Country {
  name: string;
  sigla: string;
  ddi: string;
  mask: string;
  flag?: string;
}

export let paises: Country[] = [
  {
    name: 'Brasil',
    sigla: 'BR',
    ddi: '55',
    mask: '(00) 00000-0000',
    flag: '🇧🇷',
  },
  {
    name: 'Paraguai',
    sigla: 'PY',
    ddi: '595',
    mask: '000 000000',
    flag: '🇵🇾',
  },
  {
    name: 'Argentina',
    sigla: 'AR',
    ddi: '54',
    mask: '00 0000-0000',
    flag: '🇦🇷',
  },
  {
    name: 'Colômbia',
    sigla: 'CO',
    ddi: '57',
    mask: '000 000 0000',
    flag: '🇨🇴',
  },
  {
    name: 'Uruguai',
    sigla: 'UY',
    ddi: '598',
    mask: '00 000 0000',
    flag: '🇺🇾',
  },
  { name: 'Chile', sigla: 'CL', ddi: '56', mask: '00 0000 0000', flag: '🇨🇱' },
  {
    name: 'Bolívia',
    sigla: 'BO',
    ddi: '591',
    mask: '000 000000',
    flag: '🇧🇴',
  },
  { name: 'Peru', sigla: 'PE', ddi: '51', mask: '000 000 000', flag: '🇵🇪' },
  {
    name: 'Equador',
    sigla: 'EC',
    ddi: '593',
    mask: '00 000 0000',
    flag: '🇪🇨',
  },
  {
    name: 'Venezuela',
    sigla: 'VE',
    ddi: '58',
    mask: '0000-0000000',
    flag: '🇻🇪',
  },
  { name: 'Guiana', sigla: 'GY', ddi: '592', mask: '000-0000', flag: '🇬🇾' },
  { name: 'Suriname', sigla: 'SR', ddi: '597', mask: '000-0000', flag: '🇸🇷' },
  {
    name: 'Guiana Francesa',
    sigla: 'GF',
    ddi: '594',
    mask: '00000 0000',
    flag: '🇬🇫',
  },
];
@Component({
  selector: 'app-country-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
  ],
  templateUrl: './country-picker.html',
  styleUrls: ['./country-picker.scss'],
})
export class CountryPicker {
  @Output() countrySelected = new EventEmitter<Country>();
  private _nacionalidade: string = '';
  private _paisPredefinido: Country | null = null;

  @Input()
  set nacionalidade(value: string) {
    if (!value) {
      this.countryControl.setValue(null);
      this.countryControl.enable();
      return;
    }

    this._nacionalidade = value;
    this.setCountryFromNacionalidade(value);
  }

  get nacionalidade(): string {
    return this._nacionalidade;
  }

  @Input()
  set paisPredefinido(value: Country | null) {
    if (!value) {
      this.countryControl.setValue(null);
      return;
    }

    this._paisPredefinido = value;
    this.countryControl.setValue(value);
  }

  get paisPredefinido(): string {
    return this.paisPredefinido;
  }

  countryControl = new FormControl<Country | string | null>(null);

  filteredCountries!: Observable<Country[]>;

  ngOnInit(): void {
    this.filteredCountries = this.countryControl.valueChanges.pipe(
      startWith(''),
      map((value: string | Country | null) =>
        value ? (typeof value === 'string' ? value : value.name) : ''
      ),
      map((name: string) => this._filterCountries(name))
    );
  }

  private setCountryFromNacionalidade(nac: string) {
    let country: Country | undefined | null;
    this.countryControl.enable();

    switch (nac) {
      case 'Brasileiro':
        country = paises.find((c) => c.sigla === 'BR');
        this.countryControl.disable();
        break;
      case 'Estrangeiro':
        country = null;
        this.countryControl.enable();
        break;
      default:
        country = paises.find((c) => c.name === nac);
        break;
    }

    if (country) {
      this.countryControl.setValue(country);
      this.countrySelected.emit(country);
    }
  }

  displayFn(country: Country): string {
    return country ? country.name : '';
  }

  private _filterCountries(name: string): Country[] {
    const filterValue = name.toLowerCase();
    return paises.filter((c) => c.name.toLowerCase().includes(filterValue));
  }

  onCountrySelected(event: any) {
    const country: Country = event.option.value;
    this.countrySelected.emit(country);
  }
}
